import React from 'react';
import { Link } from 'react-router-dom';
import SEO, { softwareAppSchema } from '../../Components/SEO';
import SEOPageLayout from '../../Components/SEOPageLayout';

export default function NeuralDriverPage() {
  return (
    <SEOPageLayout>
      <SEO
        title="Neural Driver — Run AI Models Locally on Your GPU | Offline AI Engine"
        description="Run powerful AI models like Qwen2-VL entirely offline with CheckScript Neural Driver. Zero latency, 100% privacy, local GPU inference."
        keywords="local AI inference, offline AI, GPU AI engine, run AI locally, Qwen2 local, Neural Driver"
        path="/features/neural-driver"
        structuredData={softwareAppSchema()}
      />

      <section className="seo-hero seo-animate">
        <div className="seo-mesh-bg"></div>
        <div className="seo-container seo-container--sm">
          <div className="seo-hero-badge">🔮 100% Offline AI</div>
          <h1 className="seo-h1">
            Unleash Local AI with <span className="seo-gradient-text">Neural Driver</span>
          </h1>
          <p className="seo-lead">
            Break free from cloud dependencies. Run state-of-the-art vision and language models directly on your hardware. Zero API costs, zero network latency, absolute privacy.
          </p>
          <div className="seo-hero-actions">
            <Link to="/download" className="seo-btn seo-btn--primary">Get Neural Driver</Link>
            <Link to="/features" className="seo-btn seo-btn--secondary">Explore All Features</Link>
          </div>
        </div>
      </section>

      <section className="seo-section seo-section--alt seo-animate">
        <div className="seo-container">
          <div className="seo-bento-grid" style={{ alignItems: 'center', gap: '64px' }}>
            <div className="seo-glass-card">
              <h3 className="seo-h3 seo-mb-24">Built-in ChatGPT-Style Interface</h3>
              <p className="seo-text-secondary">
                Neural Driver isn't just a backend engine; it hosts a complete web application on <code>Private local connection</code>. Access a beautiful, familiar chat interface right in your browser, entirely disconnected from the internet.
              </p>
              <ul className="seo-feature-list seo-mt-32">
                <li>Upload documents and images locally</li>
                <li>Chat history stored on your drive</li>
                <li>Lightning-fast token generation</li>
              </ul>
            </div>
            <div>
              <h2 className="seo-h2">The Qwen2-VL Advantage</h2>
              <p className="seo-text-secondary seo-mb-24">
                Neural Driver comes pre-configured to run advanced Vision-Language models like Qwen2-VL-7B-Int4. This means the AI doesn't just read text; it can "see" your screen, analyze charts, and understand complex UI elements—all processed on your GPU.
              </p>
              <p className="seo-text-secondary">
                By utilizing Optimized Neural Engine, we've compressed massive enterprise-grade models to run smoothly on standard consumer hardware, democratizing access to high-end AI.
              </p>
            </div>
          </div>
        </div>
      </section>

      <section className="seo-section seo-animate">
        <div className="seo-container seo-container--sm">
          <h2 className="seo-h2 seo-text-center seo-mb-48">Cloud AI vs. Neural Driver</h2>
          <div className="seo-table-wrap">
            <table className="seo-table">
              <thead>
                <tr>
                  <th>Feature</th>
                  <th>Cloud AI (ChatGPT/Claude)</th>
                  <th className="seo-highlight-col">Neural Driver (Local)</th>
                </tr>
              </thead>
              <tbody>
                <tr>
                  <td><strong>Privacy</strong></td>
                  <td>Data sent to corporate servers</td>
                  <td className="seo-highlight-col"><strong>100% Local (Never leaves PC)</strong></td>
                </tr>
                <tr>
                  <td><strong>Latency</strong></td>
                  <td>200ms+ (Network dependent)</td>
                  <td className="seo-highlight-col"><strong>&lt;10ms (Instant processing)</strong></td>
                </tr>
                <tr>
                  <td><strong>Internet Requirement</strong></td>
                  <td>Always required</td>
                  <td className="seo-highlight-col"><strong>Works completely offline</strong></td>
                </tr>
                <tr>
                  <td><strong>Cost</strong></td>
                  <td>Recurring subscription / API fees</td>
                  <td className="seo-highlight-col"><strong>Free (Uses your hardware)</strong></td>
                </tr>
                <tr>
                  <td><strong>Content Restrictions</strong></td>
                  <td>Strict corporate guardrails</td>
                  <td className="seo-highlight-col"><strong>Unrestricted local model</strong></td>
                </tr>
              </tbody>
            </table>
          </div>
        </div>
      </section>

      <section className="seo-section seo-section--alt seo-animate">
        <div className="seo-container seo-container--sm">
          <div className="seo-bento-box seo-bento-square">
            <h2 className="seo-h2 seo-text-center">System Requirements</h2>
            <p className="seo-text-center seo-text-secondary seo-mb-32">To run Neural Driver effectively, your system should meet these specifications:</p>
            <ul className="seo-feature-list" style={{ maxWidth: '400px', margin: '0 auto' }}>
              <li><strong>OS:</strong> Windows 10 or 11 (64-bit)</li>
              <li><strong>GPU:</strong> NVIDIA GPU strongly recommended</li>
              <li><strong>VRAM:</strong> Minimum 6GB VRAM (8GB+ ideal for 7B models)</li>
              <li><strong>RAM:</strong> 16GB System Memory</li>
              <li><strong>Storage:</strong> SSD with 10GB+ free space</li>
            </ul>
          </div>
        </div>
      </section>

      <section className="seo-cta seo-animate">
        <div className="seo-container">
          <div className="seo-cta-box">
            <h2 className="seo-h2">Take Control of Your AI</h2>
            <p className="seo-text-secondary seo-mb-32">Experience the privacy and speed of local inference. Download CheckScript with Neural Driver today.</p>
            <Link to="/download" className="seo-btn seo-btn--primary">Download Now</Link>
          </div>
        </div>
      </section>
    </SEOPageLayout>
  );
}
